import { useState, useMemo, useEffect, useRef } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAuth } from '../../../../../utils/Auth';
import { FieldName, IconRating, OpinionScale, RequiredToggle, RestoreButton, ArchiveButton, TextBox, TextArea, Dropdown, Count, DateTime, Signature, CheckBox, CheckList, Header, Paragraph, HeaderParagraph, Divider, Banner, SlidingScale, SingleSelect, Email, Phone, Address, CarouselField } from './FieldTypes';
import FileInput from '../../../../FileInput';
import styles from './SortableField.module.css';

// main
export default function SortableField({ field, fields, config, isAnyFieldDragging, qrTypeParent, reference, originalFields }) {

    const { listeners, transform, transition, isDragging, setNodeRef, setActivatorNodeRef } = useSortable({
        id: field.id
    });
    const time = useMemo(() => Date.now(),[]);
    const { dataDispatch, qr_data, fileHandler } = useAuth();
    const { setFiles } = fileHandler;

    const [ edit, setEdit ] = useState(null);
    // click outside of field that is editing
    const divRef = useRef(null);

    useClickOutside(divRef, () => {
        if (edit?.id === field?.id) {
            setEdit(null);
        }
    });

    const setRefs = (element) => {
        divRef.current = element;
        setNodeRef(element);
    };

    const style = transform ? {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : undefined,
    } : undefined;

    const fieldClasses = [
        styles.field,
        isAnyFieldDragging && !isDragging && styles.noHover,
        edit?.id === field?.id && styles.edit
    ].filter(Boolean).join(' ');

    const dragIconClasses = [
        styles.dragIcon,
        // handles hiding grabber icon
        isAnyFieldDragging && !isDragging && !(edit?.id === field?.id) && styles.hiddenIcon
    ].filter(Boolean).join(' ');
    const isHeaderParagraph = config?.type === "headerParagraph";

    const fieldPreview = useMemo(() => {
        switch (config?.type) {
            case 'number':
                return <Count field={field} edit={edit} />;
            case 'select':
                return <Dropdown field={field} edit={edit} qrTypeParent={qrTypeParent} />;
            case 'textarea':
                return <TextArea field={field} edit={edit} />;
            case 'email':
                return <Email field={field} edit={edit} />;
            case 'phone':
                return <Phone field={field} edit={edit} />;
            case 'address':
                return <Address field={field} edit={edit} />;
            case 'datetime-local':
                return <DateTime field={field} edit={edit} />;
            case 'radio':
                return <SingleSelect field={field} edit={edit} qrTypeParent={qrTypeParent} />;
            case 'checkbox':
                if (config.multiple) {
                    return <CheckList field={field} edit={edit} qrTypeParent={qrTypeParent} />;
                } else {
                    return <CheckBox field={field} fields={fields} config={config} edit={edit} qrTypeParent={qrTypeParent} />;
                };
            case 'icon-rating':
                return <IconRating field={field} edit={edit} qrTypeParent={qrTypeParent} />;
            case 'num-rating':
                return <OpinionScale field={field} edit={edit} qrTypeParent={qrTypeParent} />;
            case 'text':
                return <TextBox field={field} edit={edit} />;
            case 'signature':
                return <Signature />;
            case 'header':
                return (
                    <Header
                        edit={edit}
                        field={field}
                        qrTypeParent={qrTypeParent}
                    />
                );
            case 'paragraph':
                return (
                    <Paragraph
                        edit={edit}
                        field={field}
                        qrTypeParent={qrTypeParent}
                    />
                );
            case 'headerParagraph':
                return (
                    <HeaderParagraph
                        edit={edit}
                        field={field}
                        qrTypeParent={qrTypeParent}
                    />
                );
            case 'divider':
                return (
                    <Divider
                        edit={edit}
                        field={field}
                        qrTypeParent={qrTypeParent}
                    />
                );
            case 'banner':
                return (
                    <Banner
                        edit={edit}
                        field={field}
                        qrTypeParent={qrTypeParent}
                    />
                );
            case 'slide-scale':
                return (
                    <SlidingScale
                        edit={edit}
                        field={field}
                        qrTypeParent={qrTypeParent}
                    />
                );
            case 'carousel':
                return (
                    <CarouselField
                        edit={edit}
                        field={field}
                        qrTypeParent={qrTypeParent}
                    />
                );
            case 'file':
                return (
                    <FileInput
                        id={`file-field-${field.id}`}
                        name={`file-field-${field.id}`}
                        label='Upload File'
                        acceptedFileTypes={'image/*,application/pdf'}
                        disabled={true}
                        onFileSelect={() => {}}
                    />
                );
            default:
                return <TextBox field={field} edit={edit} />
        };
    },[field, config, config?.type, time, edit, qrTypeParent]);

    const handleArchive = (e) => {
        e.stopPropagation();
        if (field.archived) return;
        // If field doesn't exist in originalFields, delete instead of archive
        const fieldExistsInOriginal = originalFields?.some(f => f.id === field.id);
        if (!fieldExistsInOriginal) {
            handleDelete(e);
            return;
        }
        clearCarouselFiles();
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'archived', value: true } });
    };

    const handleRestore = (e) => {
        e.stopPropagation();
        if (!field.archived) return;
        dataDispatch({ type: qrTypeParent, data: { id: field.id, field: 'archived', value: false } });
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        clearCarouselFiles();
        dataDispatch({ type: qrTypeParent, data: { deleteId: field.id } });
    };

    const clearCarouselFiles = () => {
        if (config?.type !== 'carousel' || qrTypeParent !== 'tracker') {
            return;
        };

        const trackerFiles = qr_data?.data?.tracker?.files || [];
        if (trackerFiles.length === 0) {
            return;
        };

        const uploadedPaths = trackerFiles
            .map((file) => file?.path)
            .filter((path) => path?.includes('qr_files'));

        dataDispatch({ type: qrTypeParent, data: { files: [] } });

        if (uploadedPaths.length > 0) {
            setFiles((prev) => ({
                ...prev,
                deleteFiles: [...new Set([...prev.deleteFiles, ...uploadedPaths])],
                qrFiles: prev.qrFiles.filter((path) => !uploadedPaths.includes(path))
            }));
        };
    };

    return (
        <div ref={setRefs} style={style} className={fieldClasses} key={field.id} data-field-id={field.id} onClick={() => setEdit(field)}>
            <div ref={setActivatorNodeRef} {...listeners} className={dragIconClasses}><i className='fa-solid fa-grip-dots' /></div>
            <div className={styles.details}>
                {(!config?.readOnly && !(field.type === 'Q44D') && !isHeaderParagraph) && <FieldName field={field} fields={fields} config={config} edit={edit} qrTypeParent={qrTypeParent} />}
                {fieldPreview}
                {!field.archived && !config?.readOnly && <RequiredToggle field={field} edit={edit} qrTypeParent={qrTypeParent} />}
                {field.archived && <RestoreButton field={field} edit={edit} qrTypeParent={qrTypeParent} onRestore={handleRestore} />}
            </div>
            <ArchiveButton
                field={field}
                edit={edit}
                qrTypeParent={qrTypeParent}
                onArchive={handleArchive}
                onDelete={handleDelete}
                originalFields={originalFields}
            />
        </div>
    );
};

// different type of click outside... no states for menus
const useClickOutside = (ref, handler) => {
    useEffect(() => {
        const listener = (e) => {
            if (!ref.current || ref.current.contains(e.target)) {
                return;
            }

            handler(e)
        };

        document.addEventListener('mousedown', listener);
        document.addEventListener('touchstart', listener);

        return () => {
            document.removeEventListener('mousedown', listener);
            document.removeEventListener('touchstart', listener);
        };
    }, [ref, handler]);
};
